// src/services/auditService/auditLoanCalculatorService.ts
import {BASE_AUDIT_API, fetchAPI} from '@/services/api';

/** Request for recalculating loan schedule with user-edited values (standalone or nested under calc). */
export interface RecalculateLoanScheduleRequest {
    loanType: string;
    /** null = unset (backend falls back to schedule row); 0 is a valid override */
    prevFinalBalance: number | null;
    /** null = unset (backend falls back to schedule row); 0 is a valid override */
    currFinalBalance: number | null;
    /** Empty/omitted on calc init — backend derives from period windows + schedule. */
    currInstalments?: number[];
    remainingMonths?: number;
    annualInterestRatePercent: number;
    startInstalmentNo?: number;
    /** Interest free from MM/YYYY — secondary schedule zeros interest in range. */
    interestFreeFrom?: string | null;
    /** Interest free to MM/YYYY. */
    interestFreeTo?: string | null;
    /** Principal free from MM/YYYY — secondary schedule zeros principal in range. */
    principalFreeFrom?: string | null;
    /** Principal free to MM/YYYY. */
    principalFreeTo?: string | null;
    /** Repayment dates aligned with generated rows (inCurr then remaining). */
    repaymentDates?: string[] | null;
}

// Request from backend: AuditLoanRepaymentScheduleBO
export interface LoanScheduleRequest {
    // Loan amount in HKD
    loanAmount: number;
    // Repayment period in years
    tenorYears: number;
    // Used to calculate schedule when annualInterestRatePercent is not provided.
    totalInstalment?: number;
    /**
     * Vehicle loan: optional monthly instalment override.
     * When set, backend rebuilds the full schedule with this fixed PMT.
     */
    monthlyInstalment?: number;
    // Annual interest rate in percent, e.g. 3.5 means 3.5%
    annualInterestRatePercent?: number;
    // First repayment date (yyyy-MM-dd)
    firstRepaymentDate: string;
    /** Interest free from MM/YYYY — bank loan only; instalments in range have interest=0. */
    interestFreeFrom?: string | null;
    /** Interest free to MM/YYYY. */
    interestFreeTo?: string | null;
    /** Principal free from MM/YYYY — bank loan only; instalments in range have principal=0. */
    principalFreeFrom?: string | null;
    /** Principal free to MM/YYYY. */
    principalFreeTo?: string | null;
    /** Optional nested recalculate — calc* returns recalculatedSchedule when present. */
    recalculate?: RecalculateLoanScheduleRequest;
    /** Period windows for backend to derive inCurr when currInstalments empty. */
    periodFrom?: string | null;
    periodTo?: string | null;
    lastYearPeriodFrom?: string | null;
    lastYearPeriodTo?: string | null;
    /** JSON map instalmentNo → amount, e.g. {"12":222} */
    editedInstalments?: string | null;
}

// Item VO
export interface LoanScheduleItemVO {
    // No. of Instalment (1..N)
    instalmentNo: number;
    // Repayment date for this instalment (yyyy-MM-dd)
    repaymentDate: string;
    // Monthly instalment amount
    instalmentAmount: number;
    // Interest part of this instalment
    interestPayment: number;
    // Principal part of this instalment
    principalPayment: number;
    // Remaining outstanding balance after this instalment
    outstandingBalance: number;
}

// Response VO
export interface LoanScheduleResponse {
    // Loan Amount (HKD)
    loanAmount: number;
    // Repayment Period (Years)
    repaymentPeriodYears: number;
    // Interest rate p.a. (%)
    interestRatePercent: number;
    // List of instalments (base calc)
    schedule: LoanScheduleItemVO[];
    /** Present when calc request included recalculate / editedInstalments. */
    recalculatedSchedule?: LoanScheduleItemVO[] | null;
}

// OCR Calc Result VO - wrapper around LoanScheduleResponse with additional metadata
export interface OcrLoanCalcResult {
    bank: string;
    loanNo: string;
    loanAmt: number;
    ratePct: number;
    loanType: string;
    terms: number;
    firstRepay: string;
    lastRepay: string;
    instalment: number;
    schedule: LoanScheduleResponse;
}

const CALC_Path = `${BASE_AUDIT_API}/loan`;

// POST /audit/loan/calcBankLoanSchedule
export async function calcBankLoanSchedule(body: LoanScheduleRequest) {
    return fetchAPI<LoanScheduleResponse>(CALC_Path + `/calcBankLoanSchedule`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

// POST /audit/loan/calcVehicleLoanSchedule
export async function calcVehicleLoanSchedule(body: LoanScheduleRequest) {
    return fetchAPI<LoanScheduleResponse>(CALC_Path + `/calcVehicleLoanSchedule`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

// POST /audit/loan/recalculateLoanSchedule — live Drawer edits after init
export async function recalculateLoanSchedule(body: RecalculateLoanScheduleRequest) {
    return fetchAPI<LoanScheduleResponse>(CALC_Path + `/recalculateLoanSchedule`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

// POST /audit/loan/calcBankLoanByOcr - Upload file for OCR-based loan schedule calculation
export async function calcBankLoanByOcr(file: File): Promise<OcrLoanCalcResult> {
    const formData = new FormData();
    formData.append('file', file);

    const token = typeof localStorage !== 'undefined'
        ? (localStorage.getItem('adminToken') || localStorage.getItem('Admin-Token'))
        : null;

    const BASE_URL = (import.meta.env.VITE_APP_BASE_API as string | undefined) || '/api';
    const url = `${BASE_URL}${CALC_Path}/calcBankLoanByOcr`;
    const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
            ...(token ? { 'Admin-Token': token } : {}),
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            // Do NOT set Content-Type — browser sets multipart/form-data with boundary automatically
        },
        body: formData,
    });

    if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || 'OCR upload failed');
    }

    const json = await res.json();
    if (json.code !== undefined && json.code !== 0) {
        throw new Error(json.message || json.msg || 'OCR parsing failed');
    }
    return json.data ?? json;
}
